import { AgentService } from '@/services/agent.service'
import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react'
import { toast } from 'sonner'
import { AgentConfig, defaultAgentConfig } from '../schema'

interface AgentConfigContextValue {
  config: AgentConfig
  agentId: number | null
  updateConfig: (updates: Partial<AgentConfig>) => void
  resetConfig: () => void
  activeTab: string
  setActiveTab: (tab: string) => void
  isLoading: boolean
  isSaving: boolean
  error: string | null
  loadAgent: (id: number) => Promise<void>
  saveAgent: () => Promise<number>
}

const AgentConfigContext = createContext<AgentConfigContextValue | undefined>(
  undefined
)

export function AgentConfigProvider({
  children,
  initialConfig,
  initialAgentId,
}: {
  children: ReactNode
  initialConfig?: AgentConfig
  initialAgentId?: number
}) {
  const [config, setConfig] = useState<AgentConfig>(
    initialConfig || defaultAgentConfig
  )
  const [agentId, setAgentId] = useState<number | null>(initialAgentId || null)
  const [activeTab, setActiveTab] = useState('personality')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateConfig = useCallback((updates: Partial<AgentConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }))
  }, [])

  const resetConfig = useCallback(() => {
    setConfig(defaultAgentConfig)
    setAgentId(null)
  }, [])

  const loadAgent = useCallback(async (id: number) => {
    setIsLoading(true)
    setError(null)
    try {
      const agent = await AgentService.get(id)
      setConfig(agent)
      setAgentId(agent.id)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Erro ao carregar agente'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const saveAgent = useCallback(async (): Promise<number> => {
    setIsSaving(true)
    setError(null)
    try {
      if (agentId) {
        // Update existing agent
        await AgentService.update(agentId, config)
        toast.success('Agente atualizado com sucesso')
        return agentId
      } else {
        // Create new agent
        const created = await AgentService.create(config)
        setAgentId(created.id)
        toast.success('Agente criado com sucesso')
        return created.id
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Erro ao salvar agente'
      setError(errorMessage)
      toast.error(errorMessage)
      throw err
    } finally {
      setIsSaving(false)
    }
  }, [agentId, config])

  return (
    <AgentConfigContext.Provider
      value={{
        config,
        agentId,
        updateConfig,
        resetConfig,
        activeTab,
        setActiveTab,
        isLoading,
        isSaving,
        error,
        loadAgent,
        saveAgent,
      }}
    >
      {children}
    </AgentConfigContext.Provider>
  )
}

export function useAgentConfig() {
  const context = useContext(AgentConfigContext)
  if (!context) {
    throw new Error('useAgentConfig must be used within AgentConfigProvider')
  }
  return context
}
